import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import SendIcon from "@mui/icons-material/Send";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import AlertBox from "components/Alert/alert";

import supportRequestsTableData from "layouts/supportRequests/data/supportRequestsTableData";
import INITIAL_REQUESTS from "data/supportRequestsData";

function SupportRequests() {
  const [requests, setRequests] = useState(INITIAL_REQUESTS);
  const [page, setPage] = useState(1);

  const [alert, setAlert] = useState({ open: false, type: "success", title: "", message: "" });
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRequest, setViewRequest] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleView = (request) => {
    setViewRequest(request);
    setReplyMessage("");
    setViewOpen(true);
  };

  const handleReplySubmit = () => {
    if (!replyMessage.trim()) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Please enter a reply message.",
      });
      return;
    }
    setSending(true);
    setTimeout(() => {
      setRequests((prev) =>
        prev.map((r) => (r.id === viewRequest?.id ? { ...r, status: "replied" } : r))
      );
      setSending(false);
      setViewOpen(false);
      setAlert({
        open: true,
        type: "success",
        title: "Reply Sent",
        message: `Your reply has been emailed to ${viewRequest?.email}.`,
      });
    }, 500);
  };

  const { columns, rows } = useMemo(
    () =>
      supportRequestsTableData({
        requestList: requests,
        onView: handleView,
      }),
    [requests]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Support Requests
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 15, 20, 35, 50] }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  page={page}
                  onPageChange={setPage}
                />
              </MDBox>
            </Card>

            {/* ── Support Request Dialog (view + reply) ── */}
            <Dialog
              open={viewOpen}
              onClose={() => setViewOpen(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { borderRadius: 3 } }}
            >
              <DialogTitle sx={{ pb: 1.5 }}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <MDTypography variant="h6" fontWeight="bold">
                    Support Request
                  </MDTypography>
                  <IconButton
                    aria-label="close"
                    onClick={() => setViewOpen(false)}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </MDBox>
              </DialogTitle>

              <DialogContent dividers sx={{ pt: 3 }}>
                <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                  <MDAvatar
                    bgColor="info"
                    sx={{ fontWeight: "bold", fontSize: "0.9rem" }}
                  >
                    {viewRequest?.name?.charAt(0)}
                  </MDAvatar>
                  <MDBox flexGrow={1}>
                    <MDTypography variant="button" fontWeight="bold" display="block">
                      {viewRequest?.name}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {viewRequest?.email}
                    </MDTypography>
                  </MDBox>
                  <Chip
                    label={viewRequest?.status === "replied" ? "Replied" : "Pending"}
                    color={viewRequest?.status === "replied" ? "success" : "warning"}
                    size="small"
                    sx={{ fontWeight: "bold", textTransform: "capitalize" }}
                  />
                </MDBox>

                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                  {viewRequest?.subject}
                </MDTypography>
                <MDTypography variant="caption" color="text" mb={2} display="block">
                  Submitted on {viewRequest?.submittedAt}
                </MDTypography>

                <MDBox
                  p={2}
                  mb={3}
                  sx={{
                    backgroundColor: "grey.100",
                    borderRadius: 2,
                  }}
                >
                  <MDTypography variant="button" fontWeight="regular" color="text">
                    {viewRequest?.message}
                  </MDTypography>
                </MDBox>

                <Divider sx={{ mb: 2 }} />

                <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                  Reply to this request
                </MDTypography>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder={`Write your reply to ${viewRequest?.email}...`}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <MDBox display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    endIcon={<SendIcon fontSize="small" sx={{ color: "#fff !important" }} />}
                    onClick={handleReplySubmit}
                    disabled={sending}
                    sx={{
                      color: "#fff !important",
                      backgroundColor: "#2e7d32 !important",
                      "&:hover": { backgroundColor: "#1b5e20 !important" },
                      "& .MuiButton-endIcon": { color: "#fff !important" },
                    }}
                  >
                    {sending ? "Sending..." : "Send Reply"}
                  </Button>
                </MDBox>
              </DialogContent>
            </Dialog>
          </Grid>
        </Grid>
      </MDBox>

    </DashboardLayout>
  );
}

export default SupportRequests;
